import { useContext } from 'react';
import { ExperienceContext, useConfigService, } from '@thd-nucleus/experience-context';
import { DefaultCreditCardModal, } from '../components/shared/default-credit-card-modal/default-credit-card-modal';
import { PAYMENT_FORM_MODE, } from '../models/common/payments';
import { useModal } from './use-modal';
import { usePayments } from './use-payments';
import { b2bPaymentsApi } from '../api/b2b/payments';
import { b2cPaymentsApi } from '../api/b2c/payments';
import { paymentsApi } from '../api/common/payments';
import { CardType, NULLIFY_FIELD, cardTypeSearchMapping, } from '../constants/payments';
import { setDeleteCardAnalytics, setMeatballsAnalytics, setViewPurchasesStashEvent, } from '../utils/payments-analytics';
import { setOverlayEvent } from '../utils/analytics';
import { DeleteCardModal, } from '../components/payments/delete-card-modal/delete-card-modal';
import { useCustomerInfo } from './use-customerInfo';
import { creditCardHasMandatoryFieldsForHDPassEnroll, redirectToReloadGiftCardPage, redirectToOrderHistoryWithSearch, } from '../utils/payments';
import { AdditionalCardDetailsModal, } from '../components/payments/additional-card-details-modal/additional-card-details-modal';
import { SetInactiveModal, } from '../components/payments/set-inactive/set-inactive';
import { EditCardNicknameModal, } from '../components/payments/edit-card-nickname-modal/edit-card-nickname-modal';
import { scrollToTop } from '../utils/general';
import { CheckGiftCardBalanceModal, } from '../components/payments/check-gift-card-balance-modal/check-gift-card-balance-modal';
import { ERR_GENERIC_MODAL } from '../constants/response-error-messages';
import { RemoveHDWalletModal, } from '../components/payments/remove-home-depot-wallet/remove-home-depot-wallet';
import { useLoyaltyMembershipDetails } from './use-loyalty-membership-details';
import { CustomerRole } from '../models/common/customer';
import { useDrawer } from './use-drawer';
import { SelectPhaPermissions } from '../components/shared/select-pha-permissions/select-pha-permissions-drawer';
import { TAlertStatus } from '../models/common/alert-messages';
export const useMeatballMenuActions = () => {
    const { isB2B, creditCards, giftCards, paginationInfo, setAlertMessage, dispatchCreditCardForm, } = usePayments();
    const { getPaymentById } = usePayments();
    const { openModal, setComponentProps, hideModal } = useModal();
    const { role } = useCustomerInfo();
    const { channel } = useContext(ExperienceContext);
    const { hasHDWallet, hasProAllowance } = useLoyaltyMembershipDetails();
    const isAdmin = role === CustomerRole.ADMIN;
    const isMobile = channel === 'mobile';
    const useNewReloadUrl = !!useConfigService('ENABLE_NEW_RELOAD_GIFT_CARD_BALANCE_LINK');
    const { openDrawer, hideDrawer } = useDrawer();
    // Generic Actions
    const onViewPurchases = (card, cardType) => {
        setViewPurchasesStashEvent(cardType, card.cardBrand, card.cardType);
        redirectToOrderHistoryWithSearch(cardTypeSearchMapping[cardType], card.cardNumberLast4);
    };
    const onEditNickName = (card, currentCard, cardType, onSuccess, onFailure) => {
        const props = {
            card,
            onConfirmEdit: (cardNickName) => {
                if (!currentCard)
                    return;
                const newCard = Object.assign(Object.assign({}, currentCard), { cardNickName: cardNickName || NULLIFY_FIELD });
                setComponentProps({ isLoading: true });
                b2bPaymentsApi
                    .updatePayment(newCard, cardType)
                    .then(onSuccess)
                    .catch(onFailure)
                    .finally(() => {
                    setComponentProps({ isLoading: false });
                    hideModal();
                });
            },
            isLoading: false,
            cardType,
        };
        openModal(EditCardNicknameModal, props, { size: 'sm' });
        setMeatballsAnalytics('edit', cardType, card.cardBrand, card.cardType);
    };
    const onSetActive = (card, cardType, onSuccess, onFailure) => {
        setAlertMessage(null);
        const newActiveCard = {
            paymentId: card.paymentId,
            cardStatus: 'active',
        };
        setComponentProps({ isLoading: true });
        b2bPaymentsApi
            .updateStatus(newActiveCard, cardType)
            .then(onSuccess)
            .catch(onFailure)
            .finally(() => {
            setComponentProps({ isLoading: false });
        });
        setMeatballsAnalytics('set active', cardType, card.cardBrand, card.cardType);
    };
    const onSetInactive = (card, cardType, onSuccess, onFailure) => {
        setAlertMessage(null);
        const props = {
            card,
            onConfirmSetInactive: () => {
                const newInactiveCard = {
                    paymentId: card.paymentId,
                    cardStatus: 'inactive',
                };
                setComponentProps({ isLoading: true });
                b2bPaymentsApi
                    .updateStatus(newInactiveCard, cardType)
                    .then(onSuccess)
                    .catch(onFailure)
                    .finally(() => {
                    setComponentProps({ isLoading: false });
                    hideModal();
                });
            },
            isLoading: false,
            cardType,
            isB2B,
            hasHDWallet,
        };
        openModal(SetInactiveModal, props);
        setMeatballsAnalytics('set inactive', cardType, card.cardBrand, card.cardType);
    };
    const onDelete = (card, cardType, onSuccess, onFailure, always) => {
        const props = {
            card,
            onConfirmDelete: () => {
                setComponentProps({ isLoading: true });
                return b2bPaymentsApi
                    .deletePayment(card.cardNumber || card.paymentId, cardType)
                    .then(onSuccess)
                    .catch(onFailure)
                    .finally(() => {
                    setComponentProps({ isLoading: false });
                    hideModal();
                    // if passed than we will always call this.
                    if (always) {
                        always();
                    }
                });
            },
            isLoading: false,
            cardType,
            isB2B,
            isAdmin,
        };
        openModal(DeleteCardModal, props);
        setMeatballsAnalytics('delete', cardType, card.cardBrand, card.cardType);
        setOverlayEvent('delete checking account');
    };
    // End Generic Actions
    // Start of Credit Card Actions
    const onEditCreditCard = (card) => {
        dispatchCreditCardForm({
            type: 'open',
            mode: PAYMENT_FORM_MODE.EDIT_CREDIT_CARD,
            card,
        });
        setAlertMessage(null);
        setMeatballsAnalytics('edit', CardType.CREDIT_CARD, card.cardBrand, card.cardType);
        setOverlayEvent('edit credit card');
    };
    const onHomeDepotWallet = (card) => {
        const paymentId = card.paymentId;
        getPaymentById(paymentId, CardType.CREDIT_CARD).then((data) => {
            const hasMandatoryFields = creditCardHasMandatoryFieldsForHDPassEnroll(data);
            if (hasMandatoryFields) {
                scrollToTop();
                setMeatballsAnalytics('hd pass', CardType.CREDIT_CARD, card.cardBrand, card.cardType);
                setOverlayEvent('hdpass add card');
                dispatchCreditCardForm({
                    type: 'open',
                    mode: PAYMENT_FORM_MODE.AUTH_HD_WALLET_CREDIT_CARD,
                    card: data,
                });
            }
            else {
                const props = {
                    card,
                    onConfirmEdit: () => {
                        scrollToTop();
                        // open edit form
                        dispatchCreditCardForm({
                            type: 'open',
                            origin: PAYMENT_FORM_MODE.AUTH_HD_WALLET_CREDIT_CARD,
                            mode: PAYMENT_FORM_MODE.EDIT_CREDIT_CARD,
                            card,
                        });
                        hideModal();
                    },
                    hasHDWallet,
                };
                openModal(AdditionalCardDetailsModal, props);
            }
        });
    };
    const onRemoveHDWallet = (card, onSuccess, onFailure) => {
        setAlertMessage(null);
        const props = {
            card,
            onConfirmRemove: () => {
                setComponentProps({ isLoading: true });
                b2bPaymentsApi
                    .unEnrollmentHDWallet(card)
                    .then(onSuccess)
                    .catch((error) => {
                    onFailure(error);
                })
                    .finally(() => {
                    setComponentProps({ isLoading: false });
                    hideModal();
                    scrollToTop();
                });
            },
            isLoading: false,
        };
        openModal(RemoveHDWalletModal, props);
        setMeatballsAnalytics('remove hd pass', CardType.CREDIT_CARD, card.cardBrand, card.cardType);
    };
    const onDefaultCreditCardChange = (card, onSuccess, onFailure) => {
        setAlertMessage(null);
        const changeDefaultCreditCard = () => {
            const currentCreditCard = (creditCards === null || creditCards === void 0 ? void 0 : creditCards.response).find((payment) => payment.paymentId === card.paymentId);
            if (!currentCreditCard)
                return;
            const newDefaultPayment = Object.assign(Object.assign({}, currentCreditCard), { isDefault: true });
            setComponentProps({ isLoading: true });
            const settled = () => {
                setComponentProps({ isLoading: false });
                hideModal();
            };
            if (isB2B) {
                b2bPaymentsApi
                    .updatePayment(Object.assign(Object.assign({}, newDefaultPayment), { isActionSetAsDefault: true }), CardType.CREDIT_CARD)
                    .then(() => {
                    onSuccess();
                })
                    .catch(() => {
                    onFailure();
                })
                    .finally(settled);
            }
            else {
                b2cPaymentsApi
                    .updateCreditCard(newDefaultPayment)
                    .then(() => {
                    onSuccess();
                })
                    .catch(() => {
                    onFailure();
                })
                    .finally(settled);
            }
        };
        const props = {
            card,
            isLoading: false,
            onConfirmDefault: () => {
                changeDefaultCreditCard();
            },
        };
        openModal(DefaultCreditCardModal, props);
        setMeatballsAnalytics('set default', CardType.CREDIT_CARD, card.cardBrand, card.cardType);
        setOverlayEvent('confirm your new default payment method');
    };
    const onDeleteCreditCard = (card, onSuccess, onFailure, always) => {
        const checkForPhoneAuthAndDelete = () => {
            var _a, _b;
            const isT2CAuthorized = ((_a = card.t2cStatus) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'enrolled';
            const isT2CPending = ((_b = card.t2cStatus) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === 'pending';
            const t2cStarted = isT2CAuthorized || isT2CPending;
            if (!t2cStarted && !card.authorizeFlag) {
                return b2bPaymentsApi.deletePayment(card.paymentId, CardType.CREDIT_CARD);
            }
            const group = t2cStarted
                ? 'REMOVE_PHONE_AUTH_AND_T2C'
                : 'REMOVE_PHONE_AUTH';
            return b2bPaymentsApi
                .cnpEnrollmentRemoveAuth(card.paymentId, group)
                .then(({ data }) => {
                if (data.status !== 'SUCCESS') {
                    return Promise.reject(new Error('could not remove phone auth or t2c'));
                }
                return b2bPaymentsApi.deletePayment(card.paymentId, CardType.CREDIT_CARD);
            });
        };
        const props = {
            card,
            onConfirmDelete: () => {
                const deleteCreditCardAPI = isB2B
                    ? checkForPhoneAuthAndDelete()
                    : b2cPaymentsApi.deletePayments(card.paymentId);
                setComponentProps({ isLoading: true });
                return deleteCreditCardAPI
                    .then(() => {
                    onSuccess();
                    setDeleteCardAnalytics('credit card', card.isDefault, card.cardBrand, card.cardType);
                })
                    .catch(onFailure)
                    .finally(() => {
                    setComponentProps({ isLoading: false });
                    hideModal();
                    // if passed than we will always call this.
                    if (always) {
                        always();
                    }
                });
            },
            isLoading: false,
            cardType: CardType.CREDIT_CARD,
            isB2B,
            paginationInfo,
            isAdmin,
            hasProAllowance,
            hasHDWallet,
        };
        openModal(DeleteCardModal, props);
        setMeatballsAnalytics('delete', CardType.CREDIT_CARD, card.cardBrand, card.cardType);
    };
    const onCreditCardRemotePurchases = (card) => {
        setMeatballsAnalytics('remote purchases', CardType.CREDIT_CARD, card.cardBrand, card.cardType);
        const isABulkAddedCard = card.trackingOnly === 'Y';
        scrollToTop();
        if (isABulkAddedCard) {
            // open confirm edit modal
            const props = {
                card,
                onConfirmEdit: () => {
                    // open edit form
                    dispatchCreditCardForm({
                        type: 'open',
                        origin: PAYMENT_FORM_MODE.REMOTE_PURCHASES_CREDIT_CARD,
                        mode: PAYMENT_FORM_MODE.EDIT_CREDIT_CARD,
                        card,
                    });
                    hideModal();
                },
                hasHDWallet,
            };
            openModal(AdditionalCardDetailsModal, props);
        }
        else {
            dispatchCreditCardForm({
                type: 'open',
                mode: PAYMENT_FORM_MODE.REMOTE_PURCHASES_CREDIT_CARD,
                card,
            });
        }
    };
    const onPHARemotePurchases = (card) => {
        setMeatballsAnalytics('remote purchases', CardType.PRO_HOUSE_ACCOUNT, card.cardBrand, card.cardType, card.locType, card.t2cPrimary);
        scrollToTop();
        dispatchCreditCardForm({
            type: 'open',
            mode: PAYMENT_FORM_MODE.REMOTE_PURCHASES_PRO_HOUSE_ACCOUNT,
            card,
        });
    };
    // End of Credit Card Actions
    // ProHouseAccounts Actions
    const onProHouseAccountRemotePurchase = (card) => {
        setMeatballsAnalytics('remote purchases', CardType.PRO_HOUSE_ACCOUNT, card.cardBrand, card.cardType, card.locType, card.t2cPrimary);
        scrollToTop();
        dispatchCreditCardForm({
            type: 'open',
            mode: PAYMENT_FORM_MODE.REMOTE_PURCHASES_PRO_HOUSE_ACCOUNT,
            card,
        });
    };
    const onProHouseAccountSelectPermissions = (card) => {
        setMeatballsAnalytics('edit user permissions', CardType.PRO_HOUSE_ACCOUNT, card.cardBrand, card.cardType, card.locType, card.t2cPrimary);
        openDrawer(SelectPhaPermissions, {
            hideDrawer,
            onSuccess: (message) => {
                setAlertMessage({
                    alertStatus: TAlertStatus.SUCCESS,
                    text: message,
                });
            },
            paymentId: card.paymentId,
        });
        scrollToTop();
    };
    // Paypal Actions
    const onEditPaypal = () => {
        setMeatballsAnalytics('edit', CardType.PAYPAL_ACCOUNT, '', '');
        setOverlayEvent('edit paypal');
        return paymentsApi.paypalInit(isB2B);
    };
    const onEditPaypalNickName = (card, currentPaypalCard, onSuccess, onFailure) => {
        const props = {
            card,
            onConfirmEdit: (cardNickName) => {
                if (!currentPaypalCard)
                    return;
                const paypalCarde = Object.assign(Object.assign({}, currentPaypalCard), { cardNickName: cardNickName || NULLIFY_FIELD });
                setComponentProps({ isLoading: true });
                b2bPaymentsApi
                    .addOrUpdateCardNickName(paypalCarde, CardType.PAYPAL_ACCOUNT)
                    .then(onSuccess)
                    .catch(onFailure)
                    .finally(() => {
                    setComponentProps({ isLoading: false });
                    hideModal();
                });
            },
            isLoading: false,
            cardType: CardType.PAYPAL_ACCOUNT,
        };
        openModal(EditCardNicknameModal, props, { size: 'sm' });
        setMeatballsAnalytics('edit', CardType.PAYPAL_ACCOUNT, card.cardBrand, card.cardType);
        setOverlayEvent('edit paypal');
    };
    const openPaypalDeleteModal = (card, onSuccess, onFailure) => {
        openModal(DeleteCardModal, {
            card,
            onConfirmDelete: () => {
                setComponentProps({ isLoading: true });
                const deleteCreditCardAPI = isB2B
                    ? b2bPaymentsApi.deletePayment(card.paymentId, CardType.PAYPAL_ACCOUNT)
                    : b2cPaymentsApi.deletePayments(card.paymentId);
                return deleteCreditCardAPI
                    .then(onSuccess)
                    .catch(onFailure)
                    .finally(() => {
                    setComponentProps({ isLoading: false });
                    hideModal();
                });
            },
            cardType: CardType.PAYPAL_ACCOUNT,
            isLoading: false,
            isB2B,
            isAdmin,
        });
        setMeatballsAnalytics('delete', CardType.PAYPAL_ACCOUNT, card.cardBrand, card.cardType);
        setOverlayEvent('delete paypal');
    };
    // End Paypal Actions
    // Gift Card Actions
    const onDeleteGiftCard = (card, onSuccess, onFailure) => {
        const props = {
            card,
            onConfirmDelete: () => {
                setComponentProps({ isLoading: true });
                b2bPaymentsApi
                    .deletePayment(card.paymentId, CardType.GIFT_CARD)
                    .then(onSuccess)
                    .catch(onFailure)
                    .finally(() => {
                    setComponentProps({ isLoading: false });
                    hideModal();
                });
            },
            isLoading: false,
            cardType: CardType.GIFT_CARD,
            isB2B,
            isAdmin,
        };
        openModal(DeleteCardModal, props, { size: 'sm' });
        setMeatballsAnalytics('delete', CardType.GIFT_CARD, card.cardBrand, card.cardType);
    };
    const onReloadGiftCard = () => {
        redirectToReloadGiftCardPage(useNewReloadUrl);
    };
    const onCheckGiftCardBalance = (card, wrongPin, onSuccess, onFailure) => {
        const requestGiftCardBalance = (pin, recaptcha) => {
            setComponentProps({ isLoading: true, wrongPin: false });
            const selectedGiftCard = (giftCards === null || giftCards === void 0 ? void 0 : giftCards.response).find((payment) => payment.paymentId === card.paymentId);
            if (selectedGiftCard) {
                b2bPaymentsApi
                    .getGiftCardBalance(selectedGiftCard, pin, recaptcha)
                    .then((response) => {
                    onSuccess(response.data);
                })
                    .catch(function (error) {
                    if (error.response) {
                        const { status } = error.response;
                        //  Changing the status code 404 to 400, as per API changes [MAPKQ-462]
                        if (status === 400) {
                            setComponentProps({ wrongPin: true });
                        }
                        else {
                            const errorMessage = ERR_GENERIC_MODAL.message;
                            onFailure(errorMessage);
                        }
                    }
                })
                    .finally(() => {
                    setComponentProps({ isLoading: false });
                });
            }
        };
        const props = {
            card,
            isLoading: false,
            hideModal,
            requestGiftCardBalance,
            wrongPin,
        };
        openModal(CheckGiftCardBalanceModal, props, {
            size: isMobile ? 'sm' : 'md',
        });
    };
    const onCheckGiftCardBalanceAndReload = (onCheckBalaceAndReloadDrawerOpen) => {
        onCheckBalaceAndReloadDrawerOpen();
    };
    // End Gift Card Actions
    return {
        // Generic
        onDelete,
        onSetActive,
        onSetInactive,
        onEditNickName,
        onViewPurchases,
        // Credit Cards
        onEditCreditCard,
        onDeleteCreditCard,
        onDefaultCreditCardChange,
        onCreditCardRemotePurchases,
        onPHARemotePurchases,
        onHomeDepotWallet,
        onRemoveHDWallet,
        // Paypal Card
        onEditPaypal,
        onEditPaypalNickName,
        openPaypalDeleteModal,
        // Gift Card
        onDeleteGiftCard,
        onReloadGiftCard,
        onCheckGiftCardBalance,
        onCheckGiftCardBalanceAndReload,
        // Pro Trade Credit
        onProHouseAccountRemotePurchase,
        onProHouseAccountSelectPermissions,
    };
};
